/**
 * Per Ankh — unsent words are kept on this device until they are sent.
 *
 *   const draft = keepDraft(textarea, 'feed:compose');
 *   ...after a successful send...  draft.clear();
 *
 * A draft is saved a moment after each keystroke, restored the next time that box
 * appears empty, and forgotten once sent, after 7 days, or when the member signs
 * out (clearAllDrafts, called by the sign-out button). Drafts are filed under the
 * signed-in member's id, so on a shared phone one person never sees another's.
 *
 * Only the text is kept — never photos, and nothing is sent anywhere.
 */

const PREFIX = 'pa:draft:';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

function store() {
  try { return window.localStorage; } catch { return null; }   // private mode, blocked storage
}
function memberId() {
  return window.__paClerk?.user?.id || 'signed-in';
}
const fullKey = (key) => `${PREFIX}${memberId()}:${key}`;

export function readDraft(key) {
  const s = store();
  if (!s) return '';
  try {
    const raw = s.getItem(fullKey(key));
    if (!raw) return '';
    const { text, at } = JSON.parse(raw);
    if (!text || Date.now() - at > TTL_MS) { s.removeItem(fullKey(key)); return ''; }
    return String(text);
  } catch { return ''; }
}

export function saveDraft(key, text) {
  const s = store();
  if (!s) return;
  try {
    if (text && text.trim()) s.setItem(fullKey(key), JSON.stringify({ text, at: Date.now() }));
    else s.removeItem(fullKey(key));
  } catch { /* storage full or blocked: drafts are a convenience, never an error */ }
}

export function clearDraft(key) {
  try { store()?.removeItem(fullKey(key)); } catch { /* ignore */ }
}

/** Every draft on this device, for every member. Called on sign-out. */
export function clearAllDrafts() {
  const s = store();
  if (!s) return;
  try {
    for (let i = s.length - 1; i >= 0; i--) {
      const k = s.key(i);
      if (k && k.startsWith(PREFIX)) s.removeItem(k);
    }
  } catch { /* ignore */ }
}

/**
 * Keep a text box's draft. Restores it now if the box is empty.
 * @param {HTMLTextAreaElement|HTMLInputElement} el
 * @param {string} key   where on the site this box is, e.g. 'inbox:12'
 * @returns {{clear: () => void, restored: boolean}}
 */
export function keepDraft(el, key) {
  if (!el) return { clear() {}, restored: false };
  let restored = false;
  const saved = readDraft(key);
  if (saved && !el.value.trim()) {
    el.value = saved;
    restored = true;
    // Let the page's own listeners update counters, heights and the Send button.
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  let t = null;
  const onInput = () => {
    clearTimeout(t);
    t = setTimeout(() => saveDraft(key, el.value), 400);
  };
  // Save straight away if the page is closing mid-sentence.
  const onHide = () => { clearTimeout(t); saveDraft(key, el.value); };
  el.addEventListener('input', onInput);
  window.addEventListener('pagehide', onHide);

  return {
    restored,
    clear() {
      clearTimeout(t);
      clearDraft(key);
    },
    stop() {
      el.removeEventListener('input', onInput);
      window.removeEventListener('pagehide', onHide);
    },
  };
}
